import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { reportSummaryAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './ReportSummary.css';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import Swal from 'sweetalert2';
import { logReportExport } from '../../services/auditLogService';
import ReportIssueModal from '../StudentUniform/ReportIssueModal';

const ReportSummary = () => {
  const { t } = useTranslation(['reports', 'common']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summaryData, setSummaryData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Filters
  const [activities, setActivities] = useState([]);
  const [years, setYears] = useState([]);
  const [ppds, setPpds] = useState([]);
  const [schools, setSchools] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedPPD, setSelectedPPD] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('');
  const [activityYear, setActivityYear] = useState(''); // Year from selected activity for title

  // Report Issue modal states
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [screenshot, setScreenshot] = useState(null);

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    console.log('[ReportSummary] Component mounted');
    loadInitialData();
  }, []);

  // Fetch schools when PPD changes
  useEffect(() => {
    const fetchSchools = async () => {
      if (selectedPPD) {
        try {
          const response = await reportSummaryAPI.getSchools(selectedPPD);
          if (response.success) {
            setSchools(response.data || []);
          }
        } catch (err) {
          console.error('[ReportSummary] Error fetching schools:', err);
        }
      } else {
        setSchools([]);
      }
    };

    fetchSchools();
  }, [selectedPPD]);

  const loadInitialData = async () => {
    console.log('[ReportSummary] Loading initial data for user:', user);
    setLoading(true);

    try {
      const [activitiesResponse, yearsResponse, ppdsResponse] = await Promise.all([
        reportSummaryAPI.getActivities(),
        reportSummaryAPI.getYears(),
        reportSummaryAPI.getPPDs()
      ]);

      if (activitiesResponse.success) {
        const activityList = activitiesResponse.data || [];
        setActivities(activityList);

        if (activityList.length > 0) {
          setSelectedActivity(activityList[0].activityId.toString());
          setActivityYear(activityList[0].enrollYear.toString()); // Set year from activity
        }
      }

      if (yearsResponse.success) {
        const yearList = yearsResponse.data || [];
        setYears(yearList);

        if (yearList.length > 0) {
          setSelectedYear(yearList[0].toString());
        }
      }

      if (ppdsResponse.success) {
        const ppdList = ppdsResponse.data || [];
        setPpds(ppdList);
      }

      // Auto-select PPD and School based on user role
      let defaultPPD = '';
      let defaultSchool = '';

      const userRole = user?.role?.toUpperCase();

      if (userRole === 'HEADMASTER' || userRole === 'TEACHER') {
        // HEADMASTER/TEACHER: Auto-select their PPD and School
        if (user.ppd) {
          defaultPPD = user.ppd;
          setSelectedPPD(user.ppd);
        }
        if (user.schoolCode) {
          defaultSchool = user.schoolCode;
          setSelectedSchool(user.schoolCode);
        }
        console.log('[ReportSummary] Auto-selected for HEADMASTER/TEACHER - PPD:', defaultPPD, 'School:', defaultSchool);
      } else if (userRole === 'PPD') {
        // PPD: Auto-select their PPD, School stays as "All" (empty)
        if (user.ppd) {
          defaultPPD = user.ppd;
          setSelectedPPD(user.ppd);
        }
        console.log('[ReportSummary] Auto-selected for PPD - PPD:', defaultPPD, 'School: All');
      }

      // Load schools for the auto-selected PPD (for HEADMASTER/TEACHER/PPD)
      if (defaultPPD) {
        try {
          const schoolsResponse = await reportSummaryAPI.getSchools(defaultPPD);
          if (schoolsResponse.success) {
            setSchools(schoolsResponse.data || []);
          }
        } catch (err) {
          console.error('[ReportSummary] Error fetching schools for auto-selected PPD:', err);
        }
      }

      // Load data with auto-selected filters
      const activityList = activitiesResponse.data || [];
      if (activityList.length > 0) {
        const firstActivity = activityList[0];
        await loadSummaryDataWithActivity(firstActivity, defaultPPD, defaultSchool);
      }
    } catch (err) {
      console.error('[ReportSummary] Error loading initial data:', err);
      setError('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  // Helper function that accepts activity object directly
  const loadSummaryDataWithActivity = async (activity, ppdCode = '', schoolCode = '') => {
    const activityId = activity.activityId;
    const year = activity.enrollYear;

    console.log('[ReportSummary] Loading summary data - Activity:', activityId, 'Year:', year, 'PPD:', ppdCode, 'School:', schoolCode);
    setError('');
    setLoading(true);

    try {
      const response = await reportSummaryAPI.getSummaryData(
        activityId ? parseInt(activityId) : null,
        ppdCode || null,
        schoolCode || null,
        year ? parseInt(year) : null
      );

      if (response.success) {
        setSummaryData(response.data);
        console.log('[ReportSummary] Data loaded:', response.data);
      } else {
        setError(response.message || 'Failed to load summary data');
      }
    } catch (err) {
      console.error('[ReportSummary] Error loading summary data:', err);
      setError('An error occurred while loading summary data');
    } finally {
      setLoading(false);
    }
  };

  const loadSummaryData = async (activityId = selectedActivity, ppdCode = selectedPPD, schoolCode = selectedSchool) => {
    // Get the year from the selected activity's enrollYear
    const activity = activities.find(a => a.activityId.toString() === activityId?.toString());

    if (!activity) {
      console.error('[ReportSummary] Activity not found:', activityId);
      setError('Activity not found');
      return;
    }

    await loadSummaryDataWithActivity(activity, ppdCode, schoolCode);
  };

  const handleActivityChange = (e) => {
    const newActivity = e.target.value;
    setSelectedActivity(newActivity);

    // Update activity year for title
    const activity = activities.find(a => a.activityId.toString() === newActivity);
    if (activity) {
      setActivityYear(activity.enrollYear.toString());
    }

    loadSummaryData(newActivity, selectedPPD, selectedSchool);
  };

  const handlePPDChange = (e) => {
    const newPPD = e.target.value;
    setSelectedPPD(newPPD);
    setSelectedSchool(''); // Clear school selection when PPD changes
    setSchools([]); // Clear schools list
    loadSummaryData(selectedActivity, newPPD, '');
  };

  const handleSchoolChange = (e) => {
    const newSchool = e.target.value;
    setSelectedSchool(newSchool);
    loadSummaryData(selectedActivity, selectedPPD, newSchool);
  };

  const handleRefresh = () => {
    loadSummaryData();
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const exportToExcel = async () => {
    // Validation
    if (!selectedActivity || !selectedYear) {
      Swal.fire({
        icon: 'warning',
        title: t('reports:common.messages.selectionRequired'),
        text: t('reports:summary.messages.selectActivityYear'),
        confirmButtonColor: '#0066cc'
      });
      return;
    }

    try {
      Swal.fire({
        title: t('reports:common.messages.generatingExcel'),
        html: t('reports:common.messages.pleaseWait'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      await reportSummaryAPI.exportToExcel({
        ActivityId: parseInt(selectedActivity),
        EnrollYear: parseInt(selectedYear),
        PPDCode: selectedPPD || null,
        SchoolCode: selectedSchool || null,
        SplitBy: 0,
        IncludeSubtotals: true,
        IncludeGrandTotal: true,
        ShowPageHeaders: true,
        ApplyFormatting: true
      });

      Swal.fire({
        icon: 'success',
        title: t('reports:common.messages.exportSuccess'),
        text: t('reports:common.messages.excelDownloaded'),
        confirmButtonColor: '#28a745'
      });
    } catch (error) {
      console.error('Export error:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.exportFailed'),
        text: error.response?.data?.message || error.message || 'Unknown error',
        confirmButtonColor: '#dc3545'
      });
    }
  };

  const exportToPDF = async () => {
    // ✅ Validation: Must select Activity and Year first (same as Excel export)
    if (!selectedActivity || !selectedYear) {
      Swal.fire({
        icon: 'warning',
        title: t('reports:common.messages.selectionRequired'),
        text: t('reports:summary.messages.selectActivityYear'),
        confirmButtonColor: '#0066cc'
      });
      return;
    }

    // Check if data is available
    if (!summaryData || !summaryData.tableData || summaryData.tableData.length === 0) {
      Swal.fire({
        icon: 'info',
        title: t('common:message.noData'),
        text: t('reports:common.messages.noDataToExport'),
        confirmButtonColor: '#17a2b8'
      });
      return;
    }

    try {
      console.log('[ReportSummary] Exporting to PDF via server-side...');

      Swal.fire({
        title: t('reports:common.messages.generatingPDF'),
        text: t('reports:common.messages.pleaseWaitPDF'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Server-side PDF export with QuestPDF
      // ✅ Use PascalCase to match Excel export (both use same DTO)
      const exportPayload = {
        ActivityId: parseInt(selectedActivity),
        EnrollYear: parseInt(selectedYear),
        PPDCode: selectedPPD || null,
        SchoolCode: selectedSchool || null,
        SplitBy: 0, // None
        IncludeSubtotals: true,
        IncludeGrandTotal: true,
        ShowPageHeaders: true,
        ApplyFormatting: true
      };
      console.log('[ReportSummary] PDF export payload:', exportPayload);
      await reportSummaryAPI.exportToPdf(exportPayload);

      Swal.fire({
        icon: 'success',
        title: t('reports:common.messages.exportSuccess'),
        confirmButtonColor: '#28a745'
      });

      console.log('[ReportSummary] PDF export completed');

      // ✅ Audit Log: Log export action
      await logReportExport({
        reportName: 'Report Summary',
        exportFormat: 'PDF',
        activityId: parseInt(selectedActivity), // Validated above
        year: parseInt(selectedYear), // Validated above
        ppdCode: selectedPPD || null,
        schoolCode: selectedSchool || null,
        recordCount: summaryData?.tableData?.length || 0,
        filters: {
          activity: activities.find(a => a.activityId.toString() === selectedActivity)?.displayName || 'All',
          year: selectedYear || 'All',
          ppd: selectedPPD || 'All',
          school: selectedSchool || 'All'
        }
      });
    } catch (error) {
      console.error('[ReportSummary] Failed to export PDF:', error);
      console.error('[ReportSummary] Error response status:', error.response?.status);
      console.error('[ReportSummary] Error response data type:', error.response?.data?.constructor?.name);
      console.error('[ReportSummary] Error response data:', error.response?.data);

      // Try to extract error message from response
      let errorMessage = t('reports:common.messages.exportFailedMessage');
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          console.error('[ReportSummary] Blob text:', text);
          const errorData = JSON.parse(text);
          errorMessage = errorData.message || errorData.errorMessage || text;
          console.error('[ReportSummary] Parsed error message:', errorMessage);
        } catch (e) {
          console.error('[ReportSummary] Could not parse blob error:', e);
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        console.error('[ReportSummary] Direct error message:', errorMessage);
      } else if (error.response?.data) {
        errorMessage = JSON.stringify(error.response.data);
        console.error('[ReportSummary] Raw error data:', errorMessage);
      }

      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.exportFailed'),
        text: errorMessage,
        confirmButtonColor: '#dc3545'
      });
    }
  };

  // Handle Report Issue button click
  const handleReportIssueClick = async () => {
    try {
      Swal.fire({
        title: t('reports:common.messages.capturingScreenshot'),
        html: t('reports:common.messages.pleaseWaitScreenshot'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        width: window.innerWidth,
        height: window.innerHeight
      });

      const imageData = canvas.toDataURL('image/png');
      setScreenshot(imageData);
      setShowReportIssueModal(true);
      Swal.close();
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      Swal.fire({
        icon: 'error',
        title: t('reports:common.messages.captureFailed'),
        text: t('reports:common.messages.captureFailedMessage')
      });
    }
  };

  const overallStats = summaryData?.overallStatistics || {};
  const tableData = summaryData?.tableData || [];

  // Filter table data by search term
  const filteredData = searchTerm
    ? tableData.filter(row => row.ppd?.toLowerCase().includes(searchTerm))
    : tableData;

  return (
    <div className="report-summary-container">
      <div className="summary-content">
        {/* Header with Logo and Title */}
        <div className="header-container">
          <img className="logo" src="/KTC.png" alt="KTC" />
          <h2>{t('reports:summary.title')} ({t('reports:summary.titleEn')})</h2>
          <button className="header-btn btn-report-issue" onClick={handleReportIssueClick} title={t('common:reportIssue', { lng: 'en' })}>
            <i className="fas fa-bug"></i>
          </button>
        </div>

        {/* Summary Info Section */}
        <div className="info-section">
          <div className="info-col">
            <p className="info-row">
              <strong>{t('reports:summary.filters.district')}</strong>{' '}
              <select
                value={selectedPPD}
                onChange={handlePPDChange}
                className="activity-dropdown"
                disabled={loading}
              >
                <option value="">{t('reports:summary.all')}</option>
                {ppds.map(ppd => (
                  <option key={ppd.code} value={ppd.code}>
                    {ppd.name}
                  </option>
                ))}
              </select>
            </p>
            <p className="info-row">
              <strong>{t('reports:summary.filters.school')}</strong>{' '}
              <select
                value={selectedSchool}
                onChange={handleSchoolChange}
                className="activity-dropdown"
                disabled={loading || !selectedPPD}
              >
                <option value="">{t('reports:summary.all')}</option>
                {schools.map(school => (
                  <option key={school.code} value={school.code}>
                    {school.name}
                  </option>
                ))}
              </select>
            </p>
            <p className="info-row">
              <strong>{t('reports:summary.filters.activity')}</strong>{' '}
              <select
                value={selectedActivity}
                onChange={handleActivityChange}
                className="activity-dropdown"
                disabled={loading}
              >
                {activities.map(activity => (
                  <option key={activity.activityId} value={activity.activityId}>
                    {activity.displayName}
                  </option>
                ))}
              </select>
            </p>
            <p className="info-row">
              <strong>{t('reports:summary.stats.districtCount')}</strong> {overallStats.totalPPDs || 0}
            </p>
            <p className="info-row">
              <strong>{t('reports:summary.stats.schoolCount')}</strong> {overallStats.totalSchools || 0}
            </p>
          </div>

          <div className="info-col">
            <p className="info-row">
              <strong>{t('reports:summary.stats.male')}</strong> {(overallStats.totalMale || 0).toLocaleString()}
            </p>
            <p className="info-row">
              <strong>{t('reports:summary.stats.female')}</strong> {(overallStats.totalFemale || 0).toLocaleString()}
            </p>
            <p className="info-row">
              <strong>{t('reports:summary.stats.total')}</strong> {(overallStats.totalStudents || 0).toLocaleString()}
            </p>
          </div>

          <div className="info-col">
            <p className="info-row">
              <strong>{t('reports:summary.stats.date')}</strong> {new Date().toLocaleDateString('en-MY')} ({t('reports:summary.currentDate')})
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-bar">
          <input
            type="text"
            placeholder={t('common:placeholder.searchByDistrict')}
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
        </div>

        {/* Action Buttons */}
        <div className="action-buttons-container">
          <button className="action-btn btn-refresh" onClick={handleRefresh} disabled={loading}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
            </svg>
            {loading ? t('common:message.loading') : t('common:button.refresh')}
          </button>
          <button
            className="action-btn btn-excel"
            onClick={exportToExcel}
            disabled={loading || !selectedActivity || !selectedYear}
            title={!selectedActivity || !selectedYear ? 'Please select Activity and Year first' : ''}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12,19L8,15H10.5V12H13.5V15H16L12,19Z"/>
            </svg>
            {t('common:button.exportToExcel')}
          </button>
          <button
            className="action-btn btn-pdf"
            onClick={exportToPDF}
            disabled={loading || !selectedActivity || !selectedYear}
            title={!selectedActivity || !selectedYear ? 'Please select Activity and Year first' : ''}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10.92,12.31C10.68,11.54 10.15,9.08 11.55,9.04C12.95,9 12.03,12.16 12.03,12.16C12.42,13.65 14.05,14.72 14.05,14.72C14.55,14.57 17.4,14.24 17,15.72C16.57,17.2 13.5,15.81 13.5,15.81C11.55,15.95 10.09,16.47 10.09,16.47C8.96,18.58 7.64,19.5 7.1,18.61C6.43,17.5 9.23,16.07 9.23,16.07C10.68,13.72 10.92,12.31 10.92,12.31Z"/>
            </svg>
            {t('common:button.exportToPDF')}
          </button>
        </div>

        {/* Main Data Table */}
        <div className="table-wrapper">
          <table className="summary-table">
            <thead>
              <tr className="header-row-1">
                <th rowSpan="2" className="frozen-col">NO</th>
                <th rowSpan="2" className="frozen-col">DAERAH</th>
                <th rowSpan="2" className="frozen-col">BIL SEKOLAH</th>
                <th colSpan="7">BAJU PUTIH (KOLAR KERAS)</th>
                <th colSpan="7">SELUAR PANJANG</th>
                <th colSpan="7">SELUAR PENDEK</th>
                <th colSpan="7">BAJU KURUNG</th>
                <th colSpan="7">KAIN KURUNG</th>
                <th colSpan="7">PINAFORE</th>
                <th colSpan="14">KASUT</th>
              </tr>
              <tr className="header-row-2">
                {/* Baju Putih */}
                <th>S</th><th>M</th><th>L</th><th>XL</th><th>XXL</th><th>XXXL</th><th>JUMLAH</th>
                {/* Seluar Panjang */}
                <th>S</th><th>M</th><th>L</th><th>XL</th><th>XXL</th><th>XXXL</th><th>JUMLAH</th>
                {/* Seluar Pendek */}
                <th>S</th><th>M</th><th>L</th><th>XL</th><th>XXL</th><th>XXXL</th><th>JUMLAH</th>
                {/* Baju Kurung */}
                <th>S</th><th>M</th><th>L</th><th>XL</th><th>XXL</th><th>XXXL</th><th>JUMLAH</th>
                {/* Kain Kurung */}
                <th>S</th><th>M</th><th>L</th><th>XL</th><th>XXL</th><th>XXXL</th><th>JUMLAH</th>
                {/* Pinafore */}
                <th>S</th><th>M</th><th>L</th><th>XL</th><th>XXL</th><th>XXXL</th><th>JUMLAH</th>
                {/* Kasut */}
                <th>12</th><th>13</th><th>1</th><th>2</th><th>3</th><th>4</th><th>5</th><th>6</th><th>7</th><th>8</th><th>9</th><th>10</th><th>11</th><th>JUMLAH</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan="66" className="no-data">
                    {searchTerm ? t('common:message.noResults') : t('common:message.noDataAvailable')}
                  </td>
                </tr>
              ) : (
                filteredData.map((row, index) => (
                  <tr key={index} className={searchTerm && row.ppd?.toLowerCase().includes(searchTerm) ? 'highlight' : ''}>
                    <td className="frozen-col text-center">{row.no}</td>
                    <td className="frozen-col">{row.ppd}</td>
                    <td className="frozen-col text-right">{row.sekolah_Jumlah}</td>

                    {/* Baju Putih */}
                    <td className="text-right">{row.bajuPutih_S}</td>
                    <td className="text-right">{row.bajuPutih_M}</td>
                    <td className="text-right">{row.bajuPutih_L}</td>
                    <td className="text-right">{row.bajuPutih_XL}</td>
                    <td className="text-right">{row.bajuPutih_XXL}</td>
                    <td className="text-right">{row.bajuPutih_XXXL}</td>
                    <td className="text-right total-col">{row.bajuPutih_Jumlah}</td>

                    {/* Seluar Panjang */}
                    <td className="text-right">{row.seluarPanjang_S}</td>
                    <td className="text-right">{row.seluarPanjang_M}</td>
                    <td className="text-right">{row.seluarPanjang_L}</td>
                    <td className="text-right">{row.seluarPanjang_XL}</td>
                    <td className="text-right">{row.seluarPanjang_XXL}</td>
                    <td className="text-right">{row.seluarPanjang_XXXL}</td>
                    <td className="text-right total-col">{row.seluarPanjang_Jumlah}</td>

                    {/* Seluar Pendek */}
                    <td className="text-right">{row.seluarPendek_S}</td>
                    <td className="text-right">{row.seluarPendek_M}</td>
                    <td className="text-right">{row.seluarPendek_L}</td>
                    <td className="text-right">{row.seluarPendek_XL}</td>
                    <td className="text-right">{row.seluarPendek_XXL}</td>
                    <td className="text-right">{row.seluarPendek_XXXL}</td>
                    <td className="text-right total-col">{row.seluarPendek_Jumlah}</td>

                    {/* Baju Kurung */}
                    <td className="text-right">{row.bajuKurung_S}</td>
                    <td className="text-right">{row.bajuKurung_M}</td>
                    <td className="text-right">{row.bajuKurung_L}</td>
                    <td className="text-right">{row.bajuKurung_XL}</td>
                    <td className="text-right">{row.bajuKurung_XXL}</td>
                    <td className="text-right">{row.bajuKurung_XXXL}</td>
                    <td className="text-right total-col">{row.bajuKurung_Jumlah}</td>

                    {/* Kain Kurung */}
                    <td className="text-right">{row.kainKurung_S}</td>
                    <td className="text-right">{row.kainKurung_M}</td>
                    <td className="text-right">{row.kainKurung_L}</td>
                    <td className="text-right">{row.kainKurung_XL}</td>
                    <td className="text-right">{row.kainKurung_XXL}</td>
                    <td className="text-right">{row.kainKurung_XXXL}</td>
                    <td className="text-right total-col">{row.kainKurung_Jumlah}</td>

                    {/* Pinafore */}
                    <td className="text-right">{row.pinafore_S}</td>
                    <td className="text-right">{row.pinafore_M}</td>
                    <td className="text-right">{row.pinafore_L}</td>
                    <td className="text-right">{row.pinafore_XL}</td>
                    <td className="text-right">{row.pinafore_XXL}</td>
                    <td className="text-right">{row.pinafore_XXXL}</td>
                    <td className="text-right total-col">{row.pinafore_Jumlah}</td>

                    {/* Kasut */}
                    <td className="text-right">{row.kasut_12}</td>
                    <td className="text-right">{row.kasut_13}</td>
                    <td className="text-right">{row.kasut_1}</td>
                    <td className="text-right">{row.kasut_2}</td>
                    <td className="text-right">{row.kasut_3}</td>
                    <td className="text-right">{row.kasut_4}</td>
                    <td className="text-right">{row.kasut_5}</td>
                    <td className="text-right">{row.kasut_6}</td>
                    <td className="text-right">{row.kasut_7}</td>
                    <td className="text-right">{row.kasut_8}</td>
                    <td className="text-right">{row.kasut_9}</td>
                    <td className="text-right">{row.kasut_10}</td>
                    <td className="text-right">{row.kasut_11}</td>
                    <td className="text-right total-col">{row.kasut_Jumlah}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Loading Spinner */}
      {loading && (
        <div className="loading-spinner">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">{t('common:message.loading')}</span>
          </div>
        </div>
      )}

      {/* Report Issue Modal */}
      {showReportIssueModal && (
        <ReportIssueModal
          show={showReportIssueModal}
          onClose={() => setShowReportIssueModal(false)}
          screenshot={screenshot}
          context={{
            screen: 'Report Summary',
            selectedActivity,
            selectedYear,
            selectedPPD,
            selectedSchool,
            activityYear,
            summaryData
          }}
        />
      )}
    </div>
  );
};

export default ReportSummary;
